import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Divider,
    Chip,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUser, faTrash, faTimes, faUserCog, faCrown, faEye } from '@fortawesome/free-solid-svg-icons';
import { User, UserRole, UserWithRole } from '../types';
import apiService from '../services/api';
import { RoleUpdateModal } from './RoleUpdateModal';

interface UsersListModalProps {
    open: boolean;
    onClose: () => void;
    templateId: number;
    templateName: string;
    currentUserEmail?: string;
}

export const UsersListModal: React.FC<UsersListModalProps> = ({
    open,
    onClose,
    templateId,
    templateName,
    currentUserEmail,
}) => {
    const [users, setUsers] = useState<UserWithRole[]>([]);
    const [loading, setLoading] = useState(false);
    const [removingUsers, setRemovingUsers] = useState<number[]>([]);
    const [error, setError] = useState('');
    const [roleUpdateModal, setRoleUpdateModal] = useState<{
        open: boolean;
        user: UserWithRole | null;
    }>({ open: false, user: null });

    const fetchUsers = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiService.getTemplateUsers(templateId);
            // The API now returns users with roles in the new structure
            setUsers(response.data.users);
        } catch (err: any) {
            setError('Failed to load users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchUsers();
        }
    }, [open, templateId]);

    const handleRemoveUser = async (userWithRole: UserWithRole) => {
        setRemovingUsers(prev => [...prev, userWithRole.user.userId]);
        setError('');
        
        try {
            await apiService.removeUsersFromTemplate(templateId, [userWithRole.user.email]);
            setUsers(users.filter(u => u.user.userId !== userWithRole.user.userId));
        } catch (err: any) {
            setError('Failed to remove user');
            console.error('Error removing user:', err);
        } finally {
            setRemovingUsers(prev => prev.filter(id => id !== userWithRole.user.userId));
        }
    };

    const handleUpdateRole = async (emailId: string, role: UserRole) => {
        setError('');
        
        try {
            await apiService.updateUserRole(templateId, emailId, role);
            // Update the user's role in the local state
            setUsers(users.map(userWithRole => 
                userWithRole.user.email === emailId ? { ...userWithRole, role } : userWithRole
            ));
        } catch (err: any) {
            setError('Failed to update user role');
            console.error('Error updating user role:', err);
            throw err;
        }
    };

    const getRoleIcon = (role: UserRole) => {
        return role === 'manager' ? faCrown : faEye;
    };

    const getRoleColor = (role: UserRole) => {
        return role === 'manager' ? '#ff9800' : '#2196f3';
    };

    const handleClose = () => {
        setUsers([]);
        setError('');
        setRemovingUsers([]);
        setRoleUpdateModal({ open: false, user: null });
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    },
                }}
            >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FontAwesomeIcon icon={faUsers} />
                    Users with Access
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Users who have access to "{templateName}"
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : users.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <FontAwesomeIcon 
                            icon={faUsers} 
                            style={{ fontSize: '2rem', opacity: 0.5, marginBottom: '1rem' }} 
                        />
                        <Typography color="text.secondary">
                            No users have access to this template yet.
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ p: 0 }}>
                        {users.map((userWithRole, index) => (
                            <React.Fragment key={userWithRole.user.userId}>
                                <ListItem
                                    sx={{
                                        py: 1.5,
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                            borderRadius: 1,
                                        },
                                    }}
                                    secondaryAction={
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {currentUserEmail !== userWithRole.user.email && (
                                                <>
                                                    <Tooltip title="Update role">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setRoleUpdateModal({ open: true, user: userWithRole })}
                                                            sx={{
                                                                color: 'text.secondary',
                                                                '&:hover': {
                                                                    color: 'primary.main',
                                                                    bgcolor: 'primary.light',
                                                                },
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faUserCog} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Remove access">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveUser(userWithRole)}
                                                            disabled={removingUsers.includes(userWithRole.user.userId)}
                                                            sx={{
                                                                color: 'text.secondary',
                                                                '&:hover': {
                                                                    color: 'error.main',
                                                                    bgcolor: 'error.light',
                                                                },
                                                            }}
                                                        >
                                                            {removingUsers.includes(userWithRole.user.userId) ? (
                                                                <CircularProgress size={20} />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    }
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                                            <FontAwesomeIcon icon={faUser} />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {userWithRole.user.name}
                                                {currentUserEmail === userWithRole.user.email && (
                                                    <Chip
                                                        label="You"
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.75rem',
                                                            bgcolor: 'primary.main',
                                                            color: 'white',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {userWithRole.user.email}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <FontAwesomeIcon 
                                                        icon={getRoleIcon(userWithRole.role)} 
                                                        style={{ 
                                                            fontSize: '0.75rem',
                                                            color: getRoleColor(userWithRole.role)
                                                        }}
                                                    />
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            textTransform: 'capitalize',
                                                            color: getRoleColor(userWithRole.role),
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {userWithRole.role}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        }
                                        primaryTypographyProps={{
                                            variant: 'subtitle2',
                                            fontWeight: 500,
                                        }}
                                        secondaryTypographyProps={{
                                            variant: 'body2',
                                            color: 'text.secondary',
                                        }}
                                    />
                                </ListItem>
                                {index < users.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
                <Button onClick={handleClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>

            <RoleUpdateModal
                open={roleUpdateModal.open}
                onClose={() => setRoleUpdateModal({ open: false, user: null })}
                onSubmit={handleUpdateRole}
                userEmail={roleUpdateModal.user?.user.email || ''}
                userName={roleUpdateModal.user?.user.name || ''}
                currentRole={roleUpdateModal.user?.role}
            />
        </>
    );
}; 